import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

interface Staff {
  id: number;
  name: string;
}

interface Event {
  id?: number;
  title: string;
  date: string;
  description: string;
  type: string;
  responsible?: number;
  status?: string;
}

interface EventFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  eventToEdit?: Event | null;
}

const EventForm: React.FC<EventFormProps> = ({ onSuccess, onCancel, eventToEdit }) => {
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Official');
  const [responsible, setResponsible] = useState<number | ''>('');
  const [status, setStatus] = useState('Pending');
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await api.get('staff/?active_only=true');
        setAllStaff(response.data);
      } catch (error) {
        console.error('Failed to fetch staff', error);
      }
    };
    fetchStaff();
  }, []);

  useEffect(() => {
    if (eventToEdit) {
      setTitle(eventToEdit.title);
      // Format date for datetime-local input
      const d = new Date(eventToEdit.date);
      const formattedDate = d.toISOString().slice(0, 16);
      setDate(formattedDate);
      setDescription(eventToEdit.description);
      setType(eventToEdit.type);
      setResponsible(eventToEdit.responsible || '');
      setStatus(eventToEdit.status || 'Pending');
    } else {
      setTitle('');
      setDate('');
      setDescription('');
      setType('Official');
      setResponsible('');
      setStatus('Pending');
    }
  }, [eventToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const eventData = { 
      title, 
      date, 
      description, 
      type, 
      responsible: responsible === '' ? null : responsible,
      status 
    };

    try {
      if (eventToEdit && eventToEdit.id) {
        await api.patch(`events/${eventToEdit.id}/`, eventData);
        addToast('Evento atualizado com sucesso!', 'success');
      } else {
        await api.post('events/', eventData);
        addToast('Evento criado com sucesso!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save event', error);
      addToast('Erro ao salvar evento. Verifique os dados.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{eventToEdit ? '📅 Editar Evento' : '✨ Novo Evento Escolar'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Informações do Evento" icon="📝" />
        <div className="form-group">
          <label>Título do Evento</label>
          <input 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Ex: Festa da Família 2026"
            required 
          />
        </div>

        <div className="form-group">
          <label>Descrição / Detalhes</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Descreva o objetivo e detalhes do evento..."
            style={{ height: '100px', resize: 'vertical' }}
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Logística e Responsabilidade" icon="⚙️" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Data e Horário</label>
            <input 
              type="datetime-local" 
              value={date} 
              onChange={(e) => setDate(e.target.value)} 
              required 
            />
          </div>

          <div className="form-group">
            <label>Tipo de Evento</label>
            <select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="Official">Oficial (Calendário)</option>
              <option value="Extra">Extra / Opcional</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Colaborador Responsável</label>
            <select value={responsible} onChange={(e) => setResponsible(e.target.value === '' ? '' : parseInt(e.target.value))}>
              <option value="">Selecione um responsável...</option>
              {allStaff.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status Atual</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Pending">Pendente / Agendado</option>
              <option value="Completed">Concluído</option>
              <option value="Canceled">Cancelado</option>
              <option value="Postponed">Adiado</option>
            </select>
          </div>
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Salvando...' : 'Confirmar Evento'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
